import OpenOrderPagination from "@dana/Components/OpenOrderDetails/OpenOrderPagination";
import { isLineCancellable, notCancellableMessage } from "@dana/Components/OpenOrderDetails/OpenOrderUtils";
import { themeBackgrounds } from "@dana/MobiusOverrides/styles/colors";
import { fontWeights } from "@dana/MobiusOverrides/styles/typography";
import translate from "@insite/client-framework/Translate";
import DataTable, { DataTableProps } from "@insite/mobius/DataTable";
import {
    Checkbox,
    DataTableBody,
    DataTableCell,
    DataTableHead,
    DataTableHeader,
    DataTableRow,
    Tooltip,
} from "@insite/mobius/index";
import { TooltipPresentationProps } from "@insite/mobius/Tooltip";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC, useState } from "react";
import { css } from "styled-components";

type OrderLine = {
    lineNo: string;
    partNo: string;
    customerPartNo: string;
    description: string;
    orderedQty: number;
    cancelItemQty: number;
    cancel: boolean; // true => not cancellable (or already fully cancelled)
    notCancellableReason?: string;
};

export interface OrderLinesSelectScreenStyles {
    modalDataTable?: DataTableProps;
    disabledRow?: InjectableCss;
    tooltipText?: TooltipPresentationProps;
}

export const orderLinesSelectScreenStyles: OrderLinesSelectScreenStyles = {
    modalDataTable: {
        mergeCss: true,
        css: css`
            td {
                padding: 14px;
            }

            /* Mobile stacked layout */
            @media (max-width: 640px) {
                &,
                & thead,
                & tbody,
                & th,
                & td,
                & tr {
                    display: block;
                    width: 100%;
                }

                & thead {
                    position: absolute;
                    left: -10000px;
                    top: auto;
                    height: 0;
                    width: 1px;
                    overflow: hidden;
                }

                & tbody {
                    display: block;
                }

                & tr {
                    background: ${themeBackgrounds.white};
                    border: 1px solid ${themeBackgrounds.lightGray};
                    margin-bottom: 12px;
                    padding: 16px;
                    padding-top: 32px;
                    position: relative;
                }

                & td {
                    border: none !important;
                    padding: 5px;
                    height: auto;
                }
                & td:last-child {
                    border-bottom: none !important;
                    padding: 0;
                }

                & td > .stackedCell {
                    display: flex;
                    align-items: flex-start;
                    justify-content: space-between;
                    gap: 12px;
                    width: 100%;
                }

                & td > .stackedCell::before {
                    content: attr(data-label);
                    font-weight: 600;
                    color: ${themeBackgrounds.darkGray};
                    flex: 0 0 auto;
                    text-align: left;
                    max-width: 55%;
                }

                & td > .stackedCell .value {
                    text-align: right;
                    margin-left: auto;
                    word-break: break-word;
                    max-width: 45%;
                    text-wrap: auto;
                }

                & td > .stackedCell .inlineControl {
                    margin-left: auto;
                    position: absolute;
                    top: 10px;
                    right: 10px;
                }
            }
        `,
    },
    tooltipText: {
        mergeCss: true,
        cssOverrides: {
            tooltipBody: css`
                font-weight: ${fontWeights.regular};
            `,
            tooltipContainer: css`
                width: auto;
                max-width: unset;
                top: 50%;
                transform: translateY(-50%);
                right: 22px;
                left: unset;
                bottom: unset;
            `,
            tooltipArrow: css`
                right: -12px;
                left: unset;
                top: 50%;
                transform: translateY(-50%) rotate(-90deg);
            `,
        },
    },
    disabledRow: {
        css: css`
            td:not(.tooltip-cell) {
                opacity: 0.55;
                cursor: not-allowed;
            }
        `,
    },
};

const styles = orderLinesSelectScreenStyles;

type Props = {
    lines: OrderLine[];
    selected: Set<string>;
    onToggle: (lineNo: string) => void;
};

const OrderLinesSelectScreen: FC<Props> = ({ lines, selected, onToggle }) => {
    const L_LINE = translate("Line #");
    const L_PART = translate("Part No.");
    const L_CPART = translate("Customer Part No.");
    const L_DESC = translate("Description");
    const L_OQTY = translate("Ordered Qty");
    const L_CQTY = translate("Cancelled Qty");
    const L_CANCEL = translate("Cancel");

    const [currentPage, setCurrentPage] = useState(1);
    const [resultsPerPage, setResultsPerPage] = useState(5);
    const startIndex = (currentPage - 1) * resultsPerPage;

    return (
        <>
            <DataTable {...styles.modalDataTable}>
                <DataTableHead>
                    <DataTableHeader tight>{L_LINE}</DataTableHeader>
                    <DataTableHeader>{L_PART}</DataTableHeader>
                    <DataTableHeader>{L_CPART}</DataTableHeader>
                    <DataTableHeader>{L_DESC}</DataTableHeader>
                    <DataTableHeader alignX="right">{L_OQTY}</DataTableHeader>
                    <DataTableHeader alignX="right">{L_CQTY}</DataTableHeader>
                    <DataTableHeader alignX="center">{L_CANCEL}</DataTableHeader>
                </DataTableHead>
                <DataTableBody>
                    {lines
                        .slice()
                        .sort((a, b) => {
                            return Number(b.cancel) - Number(a.cancel);
                        })
                        .map(line => {
                            const cancellable = line.cancel;
                            const isChecked = selected.has(line.lineNo);
                            const disabledProps = !cancellable ? styles.disabledRow : {};
                            const infoText = notCancellableMessage(line);
                            return (
                                <DataTableRow key={line.partNo} {...disabledProps} aria-disabled={!cancellable}>
                                    <DataTableCell>
                                        <div className="stackedCell" data-label={L_LINE}>
                                            <span className="value">{line.lineNo}</span>
                                        </div>
                                    </DataTableCell>

                                    <DataTableCell>
                                        <div className="stackedCell" data-label={L_PART}>
                                            <span className="value">{line.partNo}</span>
                                        </div>
                                    </DataTableCell>

                                    <DataTableCell>
                                        <div className="stackedCell" data-label={L_CPART}>
                                            <span className="value">{line.customerPartNo}</span>
                                        </div>
                                    </DataTableCell>

                                    <DataTableCell>
                                        <div className="stackedCell" data-label={L_DESC}>
                                            <span className="value">{line.description}</span>
                                        </div>
                                    </DataTableCell>

                                    <DataTableCell alignX="right">
                                        <div className="stackedCell" data-label={L_OQTY}>
                                            <span className="value">{line.orderedQty}</span>
                                        </div>
                                    </DataTableCell>

                                    <DataTableCell alignX="right">
                                        <div className="stackedCell" data-label={L_CQTY}>
                                            <span className="value">{line.cancelItemQty}</span>
                                        </div>
                                    </DataTableCell>

                                    <DataTableCell alignX="center" className="tooltip-cell">
                                        <div className="stackedCell">
                                            {cancellable ? (
                                                <span className="inlineControl">
                                                    <Checkbox
                                                        checked={isChecked}
                                                        onChange={() => onToggle(line.lineNo)}
                                                        aria-label={translate("Select line to cancel")}
                                                    />
                                                </span>
                                            ) : (
                                                <Tooltip {...styles.tooltipText} text={infoText} />
                                            )}
                                        </div>
                                    </DataTableCell>
                                </DataTableRow>
                            );
                        })}
                    {lines.length === 0 && (
                        <DataTableRow>
                            <DataTableCell colSpan={7} alignX="center">
                                {translate("No lines match your search.")}
                            </DataTableCell>
                        </DataTableRow>
                    )}
                </DataTableBody>
            </DataTable>
            <>
                <OpenOrderPagination
                    totalPages={Math.ceil(lines?.length / resultsPerPage)}
                    onResultsPerPageChange={setResultsPerPage}
                    onPageChange={setCurrentPage}
                    currentPage={currentPage}
                />
            </>
        </>
    );
};

export default OrderLinesSelectScreen;
